import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, Grid, TextField } from "@mui/material";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function EventRegistrations() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState("");
  const [notification, setNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [notiColor, setNotiColor] = useState("success");

  const showNotice = (content, color = "success") => {
    setMessage(content);
    setNotiColor(color);
    setNotification(true);
    setTimeout(() => {
      setNotification(false);
      setMessage("");
      setNotiColor("success");
    }, 2500);
  };

  const loadRegistrations = () => {
    if (init() !== "success") {
      window.location.assign("/signin");
      return;
    }

    setLoading(true);

    axios
      .get(Constants.getUrls.getDksCarsBhataparaRegistrations, {
        params: {
          page,
          limit,
          search: searchText,
        },
      })
      .then((resp) => {
        if (resp?.data?.status === "success") {
          setRows(resp?.data?.data?.docs || []);
          setTotalPages(resp?.data?.data?.totalPages || 1);
          setTotalDocs(resp?.data?.data?.totalDocs || 0);
        } else {
          showNotice(resp?.data?.message || "Unable to load registrations.", "warning");
        }
      })
      .catch((err) => {
        showNotice(err?.response?.data?.message || "Unable to load registrations.", "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadRegistrations();
  }, [page, limit, searchText]);

  const updateAttendance = (registrationId, currentStatus) => {
    if (init() !== "success" || !registrationId) {
      return;
    }

    const nextStatus = currentStatus === "attended" ? "unattended" : "attended";
    setUpdatingId(registrationId);

    axios
      .put(`${Constants.putUrls.updateDksCarsBhataparaAttendance}${registrationId}`, {
        attendance_status: nextStatus,
      })
      .then((resp) => {
        if (resp?.data?.status === "success") {
          setRows((prev) =>
            prev.map((item) =>
              item?._id === registrationId
                ? { ...item, attendance_status: nextStatus }
                : item
            )
          );
          showNotice(resp?.data?.message || "Attendance updated successfully.");
        } else {
          showNotice(resp?.data?.message || "Unable to update attendance.", "warning");
        }
      })
      .catch((err) => {
        showNotice(err?.response?.data?.message || "Unable to update attendance.", "error");
      })
      .finally(() => {
        setUpdatingId("");
      });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              DKS College Bhatapara Event Registrations
            </MDTypography>
          </MDBox>

          <MDBox p={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={9}>
                <TextField
                  fullWidth
                  label="Search by name or mobile number"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <MDButton
                  variant="contained"
                  color="info"
                  fullWidth
                  onClick={() => {
                    setPage(1);
                    setSearchText(searchInput.trim());
                  }}
                >
                  Search
                </MDButton>
              </Grid>
            </Grid>
          </MDBox>

          <MDBox py={3}>
            {loading ? (
              <MDBox px={3}>
                <MDTypography variant="button" color="text">
                  Loading registrations...
                </MDTypography>
              </MDBox>
            ) : (
              <>
                <DataTable
                  table={{
                    columns: [
                      { Header: "S.No.", accessor: "serial", width: "8%" },
                      { Header: "Name", accessor: "name" },
                      { Header: "Mobile", accessor: "mobile" },
                      { Header: "College", accessor: "college_name" },
                      { Header: "Short Name", accessor: "college_short_name" },
                      { Header: "Attendance", accessor: "attendance_status" },
                      { Header: "Action", accessor: "action" },
                      { Header: "Submitted At", accessor: "createdAt" },
                    ],
                    rows: rows.map((item, index) => ({
                      serial: (page - 1) * limit + index + 1,
                      name: item?.name || "-",
                      mobile: item?.mobile || "-",
                      college_name: item?.college_name || "-",
                      college_short_name: item?.college_short_name || "-",
                      attendance_status: item?.attendance_status === "attended" ? "Attended" : "Unattended",
                      action: (
                        <MDButton
                          variant="outlined"
                          size="small"
                          color={item?.attendance_status === "attended" ? "warning" : "success"}
                          disabled={updatingId === item?._id}
                          onClick={() =>
                            updateAttendance(item?._id, item?.attendance_status || "unattended")
                          }
                        >
                          {updatingId === item?._id
                            ? "Updating..."
                            : item?.attendance_status === "attended"
                            ? "Mark Unattended"
                            : "Mark Attended"}
                        </MDButton>
                      ),
                      createdAt: formatDate(item?.createdAt),
                    })),
                  }}
                  isSorted={false}
                  entriesPerPage={{ defaultValue: limit, entries: [10, 25, 50, 100] }}
                  showTotalEntries
                  page_no={page}
                  totalPages={totalPages}
                  totalDocs={totalDocs}
                  onPageChange={setPage}
                  onEntriesPerPageChange={(nextLimit) => {
                    setLimit(nextLimit);
                    setPage(1);
                  }}
                  noEndBorder
                />
              </>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <MDSnackbar
        color={notiColor}
        icon="notifications"
        title="AgriQuiz"
        content={message}
        dateTime=""
        open={notification}
        onClose={() => setNotification(false)}
        close={() => setNotification(false)}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default EventRegistrations;
